import {
  type ScheduledConsultationDTO,
  ConsultationRequestUserRelationDTO,
  ScheduledConsultationParticipantStatusDTO,
  ScheduledConsultationStatusDTO,
  type ErrorResponseDto,
  ErrorResponseDtoCodeEnum
} from '@/generated'
import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChipProps } from '@/components/consultationrequest/ConsultationChip.vue'
import { parseISO } from 'date-fns'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { ConsultationService } from '@/services/telehealthApi/ConsultationService'
import { NoticeType } from '@/stores/models/Notice'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { useNoticeStore } from '@/stores/notice'
import { isAxiosError } from 'axios'

export function useScheduledConsultationRequest(
  scheduledConsultation: MaybeRefOrGetter<ScheduledConsultationDTO>
) {
  const noticeStore = useNoticeStore()
  const { t } = useI18n()
  const { user } = storeToRefs(useUserStore())
  const { refreshIncomingConsultationRequestList, refreshScheduledConsultationById } =
    useConsultationRequestStore()
  const consultationRequest = computed(() => toValue(scheduledConsultation))

  const consultationId = computed<string>(() => consultationRequest.value.id)
  const consultationDate = computed<Date>(() =>
    parseISO(consultationRequest.value.scheduledStartTime)
  )
  const currentDate = ref<Date>(new Date())

  function setCurrentTime(): void {
    currentDate.value = new Date()
  }

  const reportRequiredRecipients = computed(() => {
    return new Set(
      consultationRequest.value.participants
        .filter((participant) => participant.shouldWriteReport)
        .map((participant) => participant.user.userName)
    )
  })

  const reportAllowedRecipients = computed<Set<string>>(() => {
    const recipients = consultationRequest.value.participants ?? []
    const rejectReportStatuses: string[] = [
      ScheduledConsultationParticipantStatusDTO.DECLINED,
      ScheduledConsultationParticipantStatusDTO.DELETED,
      ScheduledConsultationParticipantStatusDTO.NOT_PARTICIPATED
    ]
    return new Set(
      recipients
        .filter((recipient) => !rejectReportStatuses.includes(recipient.participationStatus))
        .map((recipient) => recipient.user.userName)
    )
  })

  const isOutgoingRequest = computed<boolean>(
    () => consultationRequest.value.relationToUser === ConsultationRequestUserRelationDTO.OUTGOING
  )

  const isIncomingRequest = computed<boolean>(
    () => consultationRequest.value.relationToUser === ConsultationRequestUserRelationDTO.INCOMING
  )

  const isStatusCreated = computed<boolean>(
    () => consultationRequest.value.status === ScheduledConsultationStatusDTO.CREATED
  )

  const isStatusInProgress = computed<boolean>(
    () => consultationRequest.value.status === ScheduledConsultationStatusDTO.IN_PROGRESS
  )

  const isStatusReportsPending = computed<boolean>(
    () => consultationRequest.value.status === ScheduledConsultationStatusDTO.REPORT_PENDING
  )

  const consultationFinishedStatuses = new Set<ScheduledConsultationStatusDTO>([
    ScheduledConsultationStatusDTO.COMPLETED,
    ScheduledConsultationStatusDTO.DELETED
  ])
  const isFinishedConsultation = computed<boolean>(() =>
    consultationFinishedStatuses.has(consultationRequest.value.status)
  )

  const isStatusReportsCompleted = computed<boolean>(() => {
    return consultationRequest.value.status === ScheduledConsultationStatusDTO.REPORTS_COMPLETED
  })

  const isStatusDeleted = computed<boolean>(() => {
    return consultationRequest.value.status === ScheduledConsultationStatusDTO.DELETED
  })

  const isStatusCompleted = computed<boolean>(() => {
    return consultationRequest.value.status === ScheduledConsultationStatusDTO.COMPLETED
  })

  const isParticipationPending = computed<boolean>(() => {
    const participant = consultationRequest.value.participants.find(
      (p) => p.user.userName === user.value?.userName
    )
    return participant?.participationStatus === ScheduledConsultationParticipantStatusDTO.PENDING
  })

  const isParticipationAccepted = computed<boolean>(() => {
    const participant = consultationRequest.value.participants.find(
      (p) => p.user.userName === user.value?.userName
    )
    return participant?.participationStatus === ScheduledConsultationParticipantStatusDTO.ACCEPTED
  })

  const hasUserJoinedConsultation = computed<boolean>(() => {
    const requesterHasJoined =
      consultationRequest.value.requestedBy?.user.userName === user.value?.userName &&
      (!!consultationRequest.value.requestedBy?.joinedCallAt ||
        consultationRequest.value.isUserInCall)

    const participant = consultationRequest.value.participants.find(
      (p) => p.user.userName === user.value?.userName
    )
    const participantHasJoined =
      !!participant?.joinedCallAt ||
      !!participant?.isUserInCall ||
      (!isOutgoingRequest.value && consultationRequest.value.isUserInCall)

    return requesterHasJoined || participantHasJoined
  })

  const statusChipData = computed<{
    label: string
    icon?: string
    variant: ChipProps['variant']
  } | null>(() => {
    if (consultationRequest.value.status === ScheduledConsultationStatusDTO.IN_PROGRESS) {
      return {
        label: t('Consultation in progress'),
        icon: 'custom:phoneCall',
        variant: 'success'
      }
    }
    if (consultationRequest.value.status === ScheduledConsultationStatusDTO.REPORT_PENDING) {
      return {
        label: t('Consultation report pending'),
        variant: 'warning'
      }
    }
    if (consultationRequest.value.status === ScheduledConsultationStatusDTO.REPORTS_COMPLETED) {
      return {
        label: t('Consultation reports complete'),
        variant: 'success'
      }
    }
    if (consultationRequest.value.status === ScheduledConsultationStatusDTO.COMPLETED) {
      return {
        label: t('Completed'),
        variant: 'success'
      }
    }
    if (
      consultationRequest.value.status === ScheduledConsultationStatusDTO.DELETED &&
      consultationRequest.value.relationToUser === ConsultationRequestUserRelationDTO.INCOMING
    ) {
      return {
        label: t('Deleted by creator'),
        variant: 'error'
      }
    }

    return null
  })

  async function acceptScheduledConsultationById(id: string): Promise<void> {
    try {
      await ConsultationService.acceptScheduledConsultationRequest(id)
      await Promise.all([
        refreshIncomingConsultationRequestList(),
        refreshScheduledConsultationById(id, { withClear: false })
      ])
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Accepting the scheduled consultation request {id} failed',
        messageParameters: { id }
      })
    }
  }

  async function declineScheduledConsultationById(id: string): Promise<void> {
    try {
      await ConsultationService.declineScheduledConsultationRequest(id)
      await Promise.all([
        refreshIncomingConsultationRequestList(),
        refreshScheduledConsultationById(id, { withClear: false })
      ])
    } catch (error) {
      if (isAxiosError<ErrorResponseDto>(error) && error.response?.data) {
        const errorData = error.response.data

        if (errorData.code === ErrorResponseDtoCodeEnum.NOT_FOUND) {
          noticeStore.add({
            type: NoticeType.ERROR,
            message: t('Scheduled consultation no longer exist')
          })
          return
        } else if (errorData.code === ErrorResponseDtoCodeEnum.VALIDATION_FAILED) {
          noticeStore.add({
            type: NoticeType.ERROR,
            message: t('Scheduled consultation validation failed during decline')
          })
          return
        }
      }

      noticeStore.add({
        type: NoticeType.ERROR,
        message: t('Declining the scheduled consultation request {id} failed'),
        messageParameters: { id }
      })
    }
  }

  return {
    currentDate,
    consultationId,
    consultationDate,
    reportRequiredRecipients,
    reportAllowedRecipients,
    isFinishedConsultation,
    isOutgoingRequest,
    isIncomingRequest,
    statusChipData,
    isStatusCreated,
    isStatusInProgress,
    isStatusReportsPending,
    isStatusReportsCompleted,
    isStatusDeleted,
    isStatusCompleted,
    isParticipationPending,
    isParticipationAccepted,
    hasUserJoinedConsultation,
    setCurrentTime,
    acceptScheduledConsultationById,
    declineScheduledConsultationById
  }
}
